import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { ApiError } from "@njbsoft/api-client";
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  Pagination,
  SkeletonTable,
  portalStatusVariant,
} from "@/components/ui";
import { useToast } from "@/hooks/use-toast";
import { useActiveMembership, useMyCompanyContext } from "@/hooks/use-company-context";
import { useIsCompanyAdmin } from "@/hooks/use-company-role";
import { formatDateTime } from "@/lib/dates";
import {
  type CompanyMemberInviteRole,
  type CompanyMemberListParams,
  useCompanyMemberInvites,
  useCompanyMembers,
  useEndMember,
  useInviteMember,
  useRevokeMemberInvite,
} from "@/hooks/use-company-members";
import { useLeaveCompany } from "@/hooks/use-leave-company";
import { EndMemberDialog, type CompanyMember } from "@/components/roster/end-member-dialog";
import { InviteMemberDialog } from "@/components/roster/invite-member-dialog";
import { LeaveCompanyDialog } from "@/components/roster/leave-company-dialog";
import { buildRosterColumns, rosterSortBy } from "@/components/roster/roster-columns";
import {
  RosterFilters,
  emptyRosterFilters,
  rosterFilterParams,
  type RosterFilterState,
} from "@/components/roster/roster-filters";

export const Route = createFileRoute("/_company/company/roster/")({
  component: RosterPage,
});

const PAGE_SIZE = 20;
/** The table's column count, for a skeleton that matches what replaces it. */
const ROSTER_COLUMN_COUNT = 7;

/** The read's own default order, so the first render asks for what it sorts by. */
const DEFAULT_SORTING: SortingState = [{ id: "full_name", desc: false }];

function RosterPage() {
  const { t } = useTranslation(["company", "common"]);
  const navigate = useNavigate();
  const active = useActiveMembership();
  const isAdmin = useIsCompanyAdmin();
  const companyId = active?.company_id ?? "";
  const { success, error: errorToast, apiError } = useToast();

  // The viewer's OWN profile id, which is what tells their roster row apart
  // from everybody else's — Leave belongs to that row alone.
  const context = useMyCompanyContext();
  const selfProfileId = context.data?.profile.id ?? "";

  const [filters, setFilters] = useState<RosterFilterState>(emptyRosterFilters);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [offset, setOffset] = useState(0);
  const [endMemberTarget, setEndMemberTarget] = useState<CompanyMember | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveRefusal, setLeaveRefusal] = useState<string | null>(null);

  // A new filter or a new sort re-pages from the top. Carrying the offset over
  // would land the reader on page 3 of a result set that may only have one.
  useEffect(() => {
    setOffset(0);
  }, [filters, sorting]);

  const firstSort = sorting[0];
  const sortBy = firstSort ? rosterSortBy(firstSort.id) : undefined;
  const sortDir = firstSort?.desc === true ? "desc" : "asc";

  // Every axis rides the query: filter, sort and page are the SERVER's, which
  // is what keeps the header arrows and `total_count` honest across a roster
  // bigger than one page.
  const params = useMemo<CompanyMemberListParams>(
    () => ({
      ...rosterFilterParams(filters),
      ...(sortBy ? { sort_by: sortBy, sort_dir: sortDir } : {}),
      limit: PAGE_SIZE,
      offset,
    }),
    [filters, sortBy, sortDir, offset],
  );

  const membersQuery = useCompanyMembers(companyId, params);
  // Listing invites is itself an admin verb, so a member's page must not fire
  // it: the hook's empty-companyId guard keeps the request from leaving.
  const invitesQuery = useCompanyMemberInvites(isAdmin ? companyId : "");

  const endMemberMutation = useEndMember(companyId);
  const inviteMutation = useInviteMember(companyId);
  const revokeMutation = useRevokeMemberInvite(companyId);
  const leaveMutation = useLeaveCompany(companyId);

  const members = useMemo(() => membersQuery.data?.items ?? [], [membersQuery.data?.items]);
  const totalCount = membersQuery.data?.total_count ?? 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const isFiltered =
    filters.search.trim() !== "" ||
    filters.role !== emptyRosterFilters.role ||
    filters.state !== emptyRosterFilters.state;

  const pendingInvites = (invitesQuery.data?.items ?? []).filter((i) => i.status === "pending");

  const columns = useMemo(
    () =>
      buildRosterColumns(t, {
        // An unresolved profile id makes nobody self, which is the safe
        // direction: it hides Leave rather than offering it on a stranger's row.
        isSelf: (member) => selfProfileId !== "" && member.profile_id === selfProfileId,
        onEndMember: isAdmin ? setEndMemberTarget : undefined,
        onLeave: () => {
          setLeaveRefusal(null);
          setLeaveOpen(true);
        },
      }),
    [t, isAdmin, selfProfileId],
  );

  const handleEndMember = () => {
    if (!endMemberTarget) return;
    endMemberMutation.mutate(endMemberTarget.id, {
      onSuccess: () => {
        success("company:roster.endMemberSuccess");
        setEndMemberTarget(null);
      },
      onError: (err) => {
        const code = (err as { code?: string }).code;
        // company_last_admin gets the dedicated copy; every other 409
        // (e.g. company_member_already_ended) routes through generic apiError.
        if (code === "company_last_admin") {
          errorToast("company:roster.lastAdmin");
        } else {
          apiError(code);
        }
        setEndMemberTarget(null);
      },
    });
  };

  const handleLeave = () => {
    setLeaveRefusal(null);
    leaveMutation.mutate(undefined, {
      onSuccess: () => {
        success("company:roster.leaveSuccess");
        setLeaveOpen(false);
        // The membership this workspace exists for is gone; the shell
        // re-resolves from Home rather than leaving a roster on screen.
        void navigate({ to: "/" });
      },
      onError: (err) => {
        const code = err instanceof ApiError ? err.code : undefined;
        // The last-admin refusal is a verdict about this company's admin count
        // and belongs beside the button that produced it. The dialog stays open
        // and the roster row is untouched.
        if (code === "company_last_admin") {
          setLeaveRefusal(t("company:roster.lastAdminLeave"));
          return;
        }
        apiError(code);
        setLeaveOpen(false);
      },
    });
  };

  const handleInvite = (vars: {
    email: string;
    fullName?: string;
    role: CompanyMemberInviteRole;
  }) => {
    inviteMutation.mutate(vars, {
      onSuccess: () => {
        success("company:roster.inviteSuccess");
        setInviteOpen(false);
      },
      onError: (err) => apiError((err as { code?: string }).code),
    });
  };

  const handleRevoke = (inviteId: string) => {
    revokeMutation.mutate(inviteId, {
      onSuccess: () => success("company:roster.revokeSuccess"),
      onError: (err) => apiError((err as { code?: string }).code),
    });
  };

  if (!active) return null;

  return (
    <div className="space-y-6 p-8">
      <PageHeader
        title={t("company:roster.title")}
        subtitle={t("company:roster.subtitle", { company: active.name })}
        actions={
          isAdmin ? (
            <Button onClick={() => setInviteOpen(true)}>{t("company:roster.inviteMember")}</Button>
          ) : undefined
        }
      />

      <RosterFilters value={filters} onChange={setFilters} />

      {membersQuery.isError && (
        <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">
          {t("company:roster.loadFailed")}
        </div>
      )}

      {membersQuery.isLoading ? (
        <SkeletonTable columns={ROSTER_COLUMN_COUNT} />
      ) : members.length === 0 ? (
        <EmptyState
          title={isFiltered ? t("company:roster.noMatches") : t("company:roster.membersEmpty")}
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={members}
            sorting={sorting}
            onSortingChange={setSorting}
            onRowClick={(member) =>
              void navigate({
                to: "/company/roster/$memberId",
                params: { memberId: member.id },
              })
            }
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={PAGE_SIZE}
            onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
          />
        </>
      )}

      {/* The invite list is an admin read server-side — a member's page carries
          no invites section at all, not an empty one. It is also where the
          sidebar's pending-invite badge points, so the two read one query and
          apply one filter. */}
      {isAdmin && (
        <Card title={t("company:roster.invites")}>
          {invitesQuery.isLoading ? (
            <SkeletonTable columns={3} />
          ) : pendingInvites.length === 0 ? (
            <EmptyState variant="inline" title={t("company:roster.invitesEmpty")} />
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {pendingInvites.map((invite) => (
                <li
                  key={invite.id}
                  className="flex flex-wrap items-center justify-between gap-2 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-on-surface">
                      {invite.full_name ?? invite.email}
                    </p>
                    <p className="truncate text-xs text-muted">
                      {invite.email} · {t(`company:roster.roleLabels.${invite.role}`)} ·{" "}
                      {t("company:roster.inviteExpires", {
                        date: formatDateTime(invite.expires_at),
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* The list is filtered to pending above, so the label is
                        read literally: an accepted, revoked or expired invite
                        never reaches this row, and a variable key over statuses
                        the surface cannot render would print the raw key if one
                        ever did. */}
                    <Badge variant={portalStatusVariant("pending")}>
                      {t("company:roster.inviteStatus.pending")}
                    </Badge>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleRevoke(invite.id)}
                      disabled={revokeMutation.isPending}
                    >
                      {t("company:roster.revokeInvite")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {endMemberTarget && (
        <EndMemberDialog
          member={endMemberTarget}
          onConfirm={handleEndMember}
          onClose={() => setEndMemberTarget(null)}
          isPending={endMemberMutation.isPending}
        />
      )}
      {inviteOpen && (
        <InviteMemberDialog
          onConfirm={handleInvite}
          onClose={() => setInviteOpen(false)}
          isPending={inviteMutation.isPending}
        />
      )}
      {leaveOpen && (
        <LeaveCompanyDialog
          companyName={active.name}
          onConfirm={handleLeave}
          onClose={() => setLeaveOpen(false)}
          isPending={leaveMutation.isPending}
          refusalMessage={leaveRefusal ?? undefined}
        />
      )}
    </div>
  );
}
